import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useApi } from '../auth/ApiProvider';
import { useSession } from '../auth/SessionProvider';
import { useI18n } from '../i18n/I18nProvider';
import { LOCALE_NAMES, LOCALE_SHORT_NAMES, LOCALES } from '../i18n/types';
import { useAccount } from './Account';
import { BrandMark } from './BrandMark';
import { NotificationBell } from './NotificationBell';
import { Avatar } from './Photo';
import { Badge, Button } from './primitives';
import { ErrorState, FixtureBanner } from './states';
import { ThemeToggle } from './ThemeToggle';
import { WatermarkOnRoutes, type RouteMark } from './Watermark';

export interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Hidden unless the caller holds this permission. */
  permission?: string;
  /** An unread count, shown as a badge (FR-HOME-04). */
  count?: number;
  end?: boolean;
}

export interface AppShellProps {
  brand: string;
  /** Who or where this session is — the tenant's name, or the platform. */
  context?: string | null;
  nav: NavItem[];
  /** Phone-shaped layout, navigated from the ☰ menu at every width, for the guardian and staff apps. */
  mobile?: boolean;
  /** Where this app's notification centre is. Given, the header shows the bell (decision 2026-09-23 #25). */
  notificationsPath?: string;
  /**
   * This app's routes, marked where the screen shows a child's data; on those the content is drawn under the
   * viewer watermark (Q40, Q49). The platform console shows no child data and gives none.
   */
  watermarkRoutes?: readonly RouteMark[];
  children: ReactNode;
}

export function AppShell({
  brand, context, nav, mobile, notificationsPath, watermarkRoutes = [], children,
}: AppShellProps) {
  const { t } = useI18n();
  const { can, session, signOut } = useSession();
  const offline = useOffline();
  const account = useAccount({ enabled: session !== null });

  const visible = nav.filter((item) => !item.permission || can(item.permission));

  return (
    <div className={`dg-shell${mobile ? ' dg-shell--mobile' : ''}`}>
      <FixtureBanner />

      {/* Always visible while a platform employee is inside a tenant's data (SRS §3.5, FR-PLT-01). */}
      {session?.supportSessionId != null && (
        <div className="dg-support-banner" role="status">
          <span aria-hidden="true">🛡️</span>
          <span>{t('support.banner')}</span>
        </div>
      )}

      {session?.pendingRecovery && <RecoveryBanner requestId={session.pendingRecovery.id} />}

      {offline && (
        <p className="dg-offline-bar" role="status">
          <span aria-hidden="true">📴</span> {t('state.offline')}
        </p>
      )}

      <a className="dg-skip-link" href="#dg-main">{t('a11y.skipToContent')}</a>

      <header className="dg-topbar">
        {visible.length > 0 && <NavMenu label={brand} items={visible} />}
        <NavLink to="/" className="dg-topbar__brand" aria-label={brand}>
          <span className="dg-topbar__mark" aria-hidden="true"><BrandMark size={24} /></span>
          <span className="dg-topbar__name">{brand}</span>
        </NavLink>
        {context && <span className="dg-topbar__context">{context}</span>}
        <span className="dg-spacer" />
        <LocaleToggle />
        <ThemeToggle />
        {session && notificationsPath && <NotificationBell to={notificationsPath} />}
        {/* On a phone the account and sign-out keep only their picture; the words stay for a screen reader,
            so the header holds one line at 360px. */}
        {session && (
          <NavLink to="/account" className="dg-button dg-button--ghost" title={t('account.title')}>
            <Avatar name={account.data?.fullName} photoUrl={account.data?.photoUrl} size="sm" />
            <span className="dg-topbar__label">{t('account.title')}</span>
          </NavLink>
        )}
        {session && (
          <Button
            variant="ghost"
            title={t('auth.signOut')}
            icon={<span className="dg-topbar__icon" aria-hidden="true">🚪</span>}
            onClick={() => void signOut()}
          >
            <span className="dg-topbar__label">{t('auth.signOut')}</span>
          </Button>
        )}
      </header>

      <div className="dg-body">
        {/* On a mobile shell, and on a narrow console, this is hidden and the ☰ menu takes over; the same list
            drives both, so a navigation item can never exist in one and not the other. */}
        <nav className="dg-sidebar" aria-label={brand}>
          {visible.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className="dg-nav-link">
              <span className="dg-nav-link__icon" aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
              {item.count ? <><span className="dg-spacer" /><Badge tone="count">{item.count}</Badge></> : null}
            </NavLink>
          ))}
        </nav>

        <main className="dg-main" id="dg-main">
          <WatermarkOnRoutes marks={watermarkRoutes}>{children}</WatermarkOnRoutes>
        </main>
      </div>
    </div>
  );
}

/**
 * Every navigation item behind one ☰ button, in a panel that opens from the inline-start side (GAPS E23). It
 * replaced a bottom tab bar that showed five items and left the rest unreachable on a phone.
 *
 * A native modal `<dialog>`: it keeps the keyboard inside the panel, closes on Esc, and gives focus back to ☰.
 * While closed, ☰ carries the sum of the items' unread counts, so a waiting message is still seen.
 */
function NavMenu({ label, items }: { label: string; items: NavItem[] }) {
  const { t } = useI18n();
  const dialog = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const unread = items.reduce((sum, item) => sum + (item.count ?? 0), 0);

  const show = () => {
    dialog.current?.showModal();
    setOpen(true);
  };
  const hide = () => dialog.current?.close();

  return (
    <>
      <Button
        variant="ghost"
        className="dg-menu-button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        title={t('nav.menu')}
        onClick={show}
      >
        <span className="dg-menu-button__icon" aria-hidden="true">☰</span>
        <span className="dg-visually-hidden">{t('nav.menu')}</span>
        {unread ? <Badge tone="count">{unread}</Badge> : null}
      </Button>
      {/* A click on the dialog itself, rather than on the panel inside it, is a click on the backdrop. */}
      <dialog
        ref={dialog}
        id={panelId}
        className="dg-menu"
        aria-label={t('nav.menu')}
        onClose={() => setOpen(false)}
        onClick={(event) => { if (event.target === event.currentTarget) hide(); }}
      >
        <nav className="dg-menu__panel" aria-label={label}>
          <div className="dg-menu__head">
            <span className="dg-menu__title">{label}</span>
            <Button variant="ghost" aria-label={t('action.close')} title={t('action.close')} onClick={hide}>
              <span aria-hidden="true">✕</span>
            </Button>
          </div>
          {items.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className="dg-nav-link" onClick={hide}>
              <span className="dg-nav-link__icon" aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
              {item.count ? <><span className="dg-spacer" /><Badge tone="count">{item.count}</Badge></> : null}
            </NavLink>
          ))}
        </nav>
      </dialog>
    </>
  );
}

/** The locale after `current` in `locales`, wrapping round to the first: a third language needs no change here. */
export function nextLocale<L extends string>(current: L, locales: readonly L[]): L {
  return locales[(locales.indexOf(current) + 1) % locales.length] ?? current;
}

/**
 * Switches to the next language, as the theme button switches to the other theme. It shows that language's short
 * name, in that language ("EN" on an Arabic screen, "ع" on an English one), and is named by its full name.
 *
 * A button steps through `LOCALES`; with many languages it would become a menu (not built while there are two).
 */
export function LocaleToggle() {
  const { locale, setLocale, t } = useI18n();
  const next = nextLocale(locale, LOCALES);
  return (
    <Button variant="ghost" className="dg-locale-toggle" title={t('pref.language')} onClick={() => setLocale(next)}>
      <span aria-hidden="true" lang={next}>{LOCALE_SHORT_NAMES[next]}</span>
      <span className="dg-visually-hidden" lang={next}>{LOCALE_NAMES[next]}</span>
    </Button>
  );
}

/**
 * Whether the browser believes it is offline.
 *
 * `navigator.onLine` is only a hint — it reports the network interface, not whether the API is reachable —
 * so it drives an advisory bar and never gates a request. The staff app's queue is what actually makes
 * offline work (FR-STF-09).
 */
export function useOffline(): boolean {
  const [offline, setOffline] = useState(() => globalThis.navigator?.onLine === false);

  useEffect(() => {
    const update = () => setOffline(globalThis.navigator?.onLine === false);
    globalThis.addEventListener?.('online', update);
    globalThis.addEventListener?.('offline', update);
    return () => {
      globalThis.removeEventListener?.('online', update);
      globalThis.removeEventListener?.('offline', update);
    };
  }, []);

  return offline;
}

/**
 * Someone asked to reset this account's password with a code sent to its number, and the reset is waiting (Q25).
 * The owner, signed in here, is the one who can stop it: that is what the hold is for.
 */
function RecoveryBanner({ requestId }: { requestId: number }) {
  const api = useApi();
  const { t } = useI18n();
  const { reload } = useSession();

  const cancel = useMutation({
    mutationFn: () => api.account.cancelRecoveryRequest(requestId),
    onSuccess: () => reload(),
  });

  return (
    <div className="dg-support-banner" role="alert">
      <span aria-hidden="true">⚠️</span>
      <span>{t('recovery.banner')}</span>
      <Button variant="secondary" busy={cancel.isPending} onClick={() => cancel.mutate()}>{t('recovery.cancel')}</Button>
      {cancel.isError && <ErrorState error={cancel.error} />}
    </div>
  );
}
